import React, { useState, useEffect } from 'react';
import { Phone, Mail, MapPin, Clock, Save, Plus, Trash2, Edit, AlertCircle, MessageSquare } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import apiClient from '../../lib/api-client';
import { Switch } from '../ui/switch';

interface ContactSettings {
  supportPhone: string;
  supportEmail: string;
  supportTimings: string;
  requireBothMembersHavePlan?: boolean;
}

export default function AdminContactSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<ContactSettings>({
    supportPhone: '',
    supportEmail: '',
    supportTimings: ''
  });
  const [originalData, setOriginalData] = useState<ContactSettings>({
    supportPhone: '',
    supportEmail: '',
    supportTimings: ''
  });
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    // Check if there are any changes
    const changed = 
      formData.supportPhone !== originalData.supportPhone ||
      formData.supportEmail !== originalData.supportEmail ||
      formData.supportTimings !== originalData.supportTimings;
    setHasChanges(changed);
  }, [formData, originalData]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getSettings();
      
      if (response.success && response.data) {
        const contactInfo = response.data.contactInfo || {};
        const settings: ContactSettings = {
          supportPhone: contactInfo.supportPhone || contactInfo.phone || '+91 1800-123-4567',
          supportEmail: contactInfo.supportEmail || contactInfo.email || 'support@soulmate.com',
          supportTimings: contactInfo.supportTimings || 'Mon-Sat, 9 AM - 9 PM IST'
        };
        setFormData(settings);
        setOriginalData(settings);
      }
    } catch (error: any) {
      console.error('Error fetching settings:', error);
      // Use default values if fetch fails
      const defaultSettings = {
        supportPhone: '+91 1800-123-4567',
        supportEmail: 'support@soulmate.com',
        supportTimings: 'Mon-Sat, 9 AM - 9 PM IST'
      };
      setFormData(defaultSettings);
      setOriginalData(defaultSettings);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      // Validate email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.supportEmail)) {
        toast.error('Please enter a valid email address');
        return;
      }

      // Validate phone (basic validation)
      if (!formData.supportPhone.trim()) {
        toast.error('Please enter a support phone number');
        return;
      }

      setSaving(true);

      const response = await apiClient.updateContactInfo({
        ...formData,
        // Also update the main contact fields
        phone: formData.supportPhone,
        email: formData.supportEmail
      });

      if (response.success) {
        toast.success('Contact settings updated successfully');
        setOriginalData(formData);
        setHasChanges(false);
      }
    } catch (error: any) {
      console.error('Error saving contact settings:', error);
      toast.error(error.response?.data?.message || 'Failed to update contact settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setFormData(originalData);
    setHasChanges(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold">Help & Support Contact Settings</h2>
        <p className="text-gray-600 mt-1">
          Configure support contact information displayed in the Help section
        </p>
      </div>

      {/* Contact Settings Form */}
      <Card>
        <CardHeader>
          <CardTitle>Support Contact Details</CardTitle>
          <CardDescription>
            Update the contact information shown to users seeking help
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Support Phone */}
          <div className="space-y-2">
            <Label htmlFor="supportPhone" className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-blue-600" />
              Support Phone Number
            </Label>
            <Input
              id="supportPhone"
              placeholder="+91 1800-123-4567"
              value={formData.supportPhone}
              onChange={(e) => setFormData({ ...formData, supportPhone: e.target.value })}
            />
            <p className="text-xs text-gray-500">
              Include country code. This number will be displayed on contact cards.
            </p>
          </div>

          {/* Support Email */}
          <div className="space-y-2">
            <Label htmlFor="supportEmail" className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-purple-600" />
              Support Email Address
            </Label>
            <Input
              id="supportEmail"
              type="email"
              placeholder="support@soulmate.com"
              value={formData.supportEmail}
              onChange={(e) => setFormData({ ...formData, supportEmail: e.target.value })}
            />
            <p className="text-xs text-gray-500">
              Users can send inquiries to this email address.
            </p>
          </div>

          {/* Support Timings */}
          <div className="space-y-2">
            <Label htmlFor="supportTimings" className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-green-600" />
              Support Availability
            </Label>
            <Input
              id="supportTimings"
              placeholder="Mon-Sat, 9 AM - 9 PM IST"
              value={formData.supportTimings}
              onChange={(e) => setFormData({ ...formData, supportTimings: e.target.value })}
            />
            <p className="text-xs text-gray-500">
              Let users know when support is available (e.g., "24/7", "Mon-Fri, 9 AM - 6 PM").
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Preview Card */}
      <Card>
        <CardHeader>
          <CardTitle>Preview</CardTitle>
          <CardDescription>
            This is how your contact information will appear to users
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Live Chat Preview */}
            <Card className="border-2">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-100">
                    <Mail className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <CardTitle className="text-base">Live Chat</CardTitle>
                    <CardDescription className="text-xs">Available 24/7</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Button className="w-full bg-rose-600 hover:bg-rose-700">
                  Start Chat
                </Button>
              </CardContent>
            </Card>

            {/* Call Us Preview */}
            <Card className="border-2">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100">
                    <Phone className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle className="text-base">Call Us</CardTitle>
                    <CardDescription className="text-xs">
                      {formData.supportTimings}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Button variant="outline" className="w-full">
                  {formData.supportPhone}
                </Button>
              </CardContent>
            </Card>

            {/* Email Support Preview */}
            <Card className="border-2">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-100">
                    <Mail className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <CardTitle className="text-base">Email Support</CardTitle>
                    <CardDescription className="text-xs">Response in 24 hours</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Button variant="outline" className="w-full text-xs truncate">
                  {formData.supportEmail}
                </Button>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      {hasChanges && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-amber-600" />
                <span className="font-medium text-amber-900">You have unsaved changes</span>
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={handleReset} disabled={saving}>
                  Reset
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-rose-600 hover:bg-rose-700"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}